import React from "react";
import { cn } from "@/utils";
import { LAB, NUM } from "@/components/ms";

/**
 * THE ledger table — `Web Screens.html` #v-ledger (.tbl, lines 121–148).
 *
 * Below 900px every row is the phone row: a flex line of coin mark, what
 * happened (+ mono sub), tag and value. From 900px the same markup becomes a
 * real table and the withheld columns (date, source, quantity, cost basis,
 * gain) appear, because a wide viewport can hold them without shrinking
 * anything.
 *
 * Local to the ledger area — the shared kit has no table primitive.
 */

const TD =
  "ms900:table-cell ms900:border-t ms900:border-ms-line ms900:py-[11px] ms900:pr-3 ms900:align-middle";

/** Cell classes. `meta`/`num` are the columns that only exist from 900px. */
export const CELL = {
  /** coin / brand mark */
  asset: cn("block flex-none", TD),
  /** what happened — the flexible column */
  what: cn("block min-w-0 flex-1 text-[13.5px] tracking-[-0.01em]", TD),
  /** the mono sub-line under `what`, phone only */
  sub: cn(LAB, "mt-[3px] block text-[9px] text-ms-dim ms900:hidden"),
  /** date / source — withheld below 900 */
  meta: cn("hidden whitespace-nowrap font-mono text-[11.5px] text-ms-mute", TD),
  /** quantity / cost basis / gain — withheld below 900, right aligned */
  num: cn("hidden text-right text-[12.5px]", NUM, TD),
  tag: cn("block flex-none", TD),
  /** the value that survives at every width */
  val: cn(
    "block whitespace-nowrap text-right text-[13.5px] font-medium",
    NUM,
    TD
  ),
};

export type LedgerCol = {
  label?: React.ReactNode;
  /** right-align the head (numeric columns) */
  right?: boolean;
  width?: number;
};

export const LedgerTable = ({
  cols,
  children,
  className,
}: {
  cols: LedgerCol[];
  children: React.ReactNode;
  className?: string;
}) => (
  <table className={cn("w-full border-collapse", className)}>
    <thead className="hidden ms900:table-header-group">
      <tr>
        {cols.map((c, i) => (
          <th
            key={i}
            style={c.width ? { width: c.width } : undefined}
            className={cn(
              LAB,
              "whitespace-nowrap border-b border-ms-ink py-2 pr-3 text-[9px] font-medium tracking-[0.11em] text-ms-dim",
              c.right ? "text-right" : "text-left"
            )}
          >
            {c.label}
          </th>
        ))}
      </tr>
    </thead>
    <tbody>{children}</tbody>
  </table>
);

export const LedgerRow = ({
  children,
  className,
  ...rest
}: React.HTMLAttributes<HTMLTableRowElement>) => (
  <tr
    {...rest}
    className={cn(
      "flex items-center gap-3 border-t border-ms-line py-[11px] transition-colors first:border-t-0",
      "ms900:table-row ms900:border-t-0 ms900:py-0",
      "hover:bg-ms-card2",
      className
    )}
  >
    {children}
  </tr>
);

/** The 2px-ink month separator with its mono summary line. */
export const MonthRow = ({
  span,
  children,
}: {
  span: number;
  children: React.ReactNode;
}) => (
  <tr className="block border-t-2 border-t-ms-ink pt-3.5 ms900:table-row ms900:border-t-0 ms900:pt-0">
    <td
      colSpan={span}
      className={cn(
        LAB,
        "block pb-2 text-[9.5px] text-ms-mute",
        "ms900:table-cell ms900:border-t-2 ms900:border-t-ms-ink ms900:pb-2 ms900:pt-3.5"
      )}
    >
      {children}
    </td>
  </tr>
);

/** A full-width row for empty / loading / error states inside the table. */
export const LedgerFullRow = ({
  span,
  children,
  className,
}: {
  span: number;
  children: React.ReactNode;
  className?: string;
}) => (
  <tr className="block ms900:table-row">
    <td
      colSpan={span}
      className={cn("block border-t border-ms-line py-8 ms900:table-cell", className)}
    >
      {children}
    </td>
  </tr>
);
